import * as React from 'react';
import { differenceInCalendarDays, format, getDay, parse, startOfWeek } from 'date-fns';
import { enGB } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import {
  Calendar,
  dateFnsLocalizer,
  type CalendarProps,
  type DateRange,
  type Event as BigCalendarEvent,
  type View,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useSearchParams } from 'react-router-dom';
import { ConditionElement, Entity, getFilter, Row, Sort } from 'gossamer-unified';
import { useCacheQuery } from '../data/useCacheQuery';
import styles from './CalendarView.module.css';

// shared react-big-calendar localizer, for calendars that don't use CalendarView
export const calendarLocalizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: {
    'en-GB': enGB,
  },
});

const DragAndDropCalendar = withDragAndDrop<CalendarViewEvent>(Calendar);

const getStartOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);

const getEndOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);

const getMonthRange = (date: Date): { start: Date; end: Date } => ({
  start: new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0),
  end: new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999),
});

const getValidDate = (value: string | null): Date | undefined => {
  if (!value) {
    return undefined;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const getRangeBounds = (range: Date[] | { start: Date; end: Date }): { start: Date; end: Date } => {
  if (Array.isArray(range)) {
    return {
      start: getStartOfDay(range[0]),
      end: getEndOfDay(range[range.length - 1]),
    };
  }
  return {
    start: getStartOfDay(range.start),
    end: getEndOfDay(range.end),
  };
};

export interface CalendarRange {
  start: Date;
  end: Date;
}

export type CalendarViewMode = 'month' | 'week' | 'day' | 'agenda';

const CALENDAR_VIEW_SEARCH_PARAM = 'calendarView';
const DEFAULT_CALENDAR_VIEW: CalendarViewMode = 'month';
const CALENDAR_VIEWS: CalendarViewMode[] = ['month', 'week', 'day', 'agenda'];

const isCalendarViewMode = (value: string | null): value is CalendarViewMode =>
  !!value && CALENDAR_VIEWS.includes(value as CalendarViewMode);

const inferCalendarViewFromRange = (start: Date, end: Date): CalendarViewMode => {
  const daySpan = differenceInCalendarDays(end, start);
  if (daySpan < 0) {
    return DEFAULT_CALENDAR_VIEW;
  }
  if (daySpan === 0) {
    return 'day';
  }
  if (daySpan <= 7) {
    return 'week';
  }
  return 'month';
};

const getCalendarViewFromSearchParams = (searchParams: URLSearchParams): CalendarViewMode => {
  const view = searchParams.get(CALENDAR_VIEW_SEARCH_PARAM);
  if (isCalendarViewMode(view)) {
    return view;
  }
  const start = getValidDate(searchParams.get('start'));
  const end = getValidDate(searchParams.get('end'));
  if (start && end) {
    return inferCalendarViewFromRange(start, end);
  }
  return DEFAULT_CALENDAR_VIEW;
};

const getCalendarRangeFromSearchParams = (searchParams: URLSearchParams): CalendarRange => {
  const start = getValidDate(searchParams.get('start'));
  const end = getValidDate(searchParams.get('end'));
  if (start && end) {
    return { start, end };
  }
  return getMonthRange(new Date());
};

export interface CalendarViewEvent extends BigCalendarEvent {
  id: string;
  start: Date;
  end: Date;
}

export interface CalendarViewProps<TEvent extends CalendarViewEvent = CalendarViewEvent> {
  events: TEvent[];
  eventPropGetter?: (
    event: TEvent,
    start: Date,
    end: Date,
    isSelected: boolean,
  ) => React.HTMLAttributes<HTMLDivElement>;
  // if supplied, events are draggable (not resizable) and this is called on drop with the new start and end
  handleEventDrop?: (event: TEvent, start: Date, end: Date) => void;
  // if supplied, events are resizable in week/day views (not month, where resizing changes the dates)
  handleEventResize?: (event: TEvent, start: Date, end: Date) => void;
  handleEventSelect?: (event: TEvent) => void;
  // with handleEventDrop/handleEventResize - which events can be moved or resized (default all)
  isDraggable?: (event: TEvent) => boolean;
  handleSlotSelect?: (slotInfo: {
    start: Date;
    end: Date;
    slots: Date[];
    action?: 'select' | 'click' | 'doubleClick';
  }) => void;
  selected?: TEvent;
  // minutes per slot in week/day views, and slots per labelled time group - react-big-calendar defaults 30 and 2
  step?: number;
  timeslots?: number;
}

export const useCalendarRange = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const range = React.useMemo(() => getCalendarRangeFromSearchParams(searchParams), [searchParams]);
  const view = React.useMemo(() => getCalendarViewFromSearchParams(searchParams), [searchParams]);

  const setCalendarState = React.useCallback(
    (start: Date, end: Date, nextView: CalendarViewMode) => {
      const nextStart = getStartOfDay(start).toISOString();
      const nextEnd = getEndOfDay(end).toISOString();
      const currentStart = searchParams.get('start');
      const currentEnd = searchParams.get('end');
      const currentView = searchParams.get(CALENDAR_VIEW_SEARCH_PARAM);
      if (currentStart === nextStart && currentEnd === nextEnd && currentView === nextView) {
        return;
      }
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set('start', nextStart);
      nextSearchParams.set('end', nextEnd);
      nextSearchParams.set(CALENDAR_VIEW_SEARCH_PARAM, nextView);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setRange = React.useCallback(
    (start: Date, end: Date) => {
      setCalendarState(start, end, view);
    },
    [setCalendarState, view],
  );

  const setView = React.useCallback(
    (nextView: CalendarViewMode) => {
      setCalendarState(range.start, range.end, nextView);
    },
    [range.end, range.start, setCalendarState],
  );

  React.useEffect(() => {
    const hasValidRange = !!getValidDate(searchParams.get('start')) && !!getValidDate(searchParams.get('end'));
    const hasValidView = isCalendarViewMode(searchParams.get(CALENDAR_VIEW_SEARCH_PARAM));
    if (hasValidRange && hasValidView) {
      return;
    }
    setCalendarState(range.start, range.end, view);
  }, [range.end, range.start, searchParams, setCalendarState, view]);

  return { range, view, setRange, setView, setCalendarState };
};

// maximum rows loaded for one calendar page
const CALENDAR_QUERY_LIMIT = 1000;

// rows of an entity with start_at and end_at fields that overlap the calendar's visible range, and match the given
// conditions - the previous rows are returned while a new range is loading, so that the calendar doesn't go blank
export const useCalendarRangeQuery = <T extends Row>(
  entity: Entity<T>,
  conditions: ConditionElement[] = [],
  order?: Sort[],
) => {
  const { range } = useCalendarRange();
  const query = useCacheQuery(entity, {
    where: {
      and: [
        ...conditions,
        getFilter('end_at', 'gt', range.start.toISOString()),
        getFilter('start_at', 'lt', range.end.toISOString()),
      ],
    },
    order,
    limit: CALENDAR_QUERY_LIMIT,
  });
  const [stableRows, setStableRows] = React.useState<T[]>([]);
  React.useEffect(() => {
    if (query.data !== undefined) {
      setStableRows(query.data);
    }
  }, [query.data]);
  return { rows: query.data ?? stableRows, query };
};

export const CalendarView = <TEvent extends CalendarViewEvent>(props: CalendarViewProps<TEvent>): React.JSX.Element => {
  const { range, view, setView, setCalendarState } = useCalendarRange();
  // Keep pendingViewRef in sync with the current view on every render so that
  // onRangeChange (fired by navigation arrows) always uses the correct view.
  // When onView fires (view-button click), we update it eagerly before the
  // re-render so that the subsequent onRangeChange call sees the new view.
  const pendingViewRef = React.useRef<CalendarViewMode>(view);
  pendingViewRef.current = view;

  const defaultDate = React.useMemo(
    () => new Date((range.start.getTime() + range.end.getTime()) / 2),
    [range.end, range.start],
  );

  const calendarProps: CalendarProps<TEvent> = {
    localizer: calendarLocalizer,
    culture: 'en-GB',
    events: props.events,
    startAccessor: 'start',
    endAccessor: 'end',
    defaultDate,
    view,
    eventPropGetter: props.eventPropGetter,
    views: ['month', 'week', 'day', 'agenda'],
    onView: (nextView: View) => {
      if (isCalendarViewMode(nextView)) {
        pendingViewRef.current = nextView;
        setView(nextView);
      }
    },
    onRangeChange: (range: Date[] | DateRange) => {
      const nextRange = getRangeBounds(range);
      setCalendarState(nextRange.start, nextRange.end, pendingViewRef.current);
    },
    onSelectEvent: (event) => {
      if (props.handleEventSelect) {
        props.handleEventSelect(event);
      }
    },
    selectable: !!props.handleSlotSelect,
    onSelectSlot: (slotInfo) => {
      if (props.handleSlotSelect) {
        props.handleSlotSelect(slotInfo);
      }
    },
    selected: props.selected,
    step: props.step,
    timeslots: props.timeslots,
  };

  return (
    <div className={styles.CalendarView}>
      {props.handleEventDrop ? (
        <DragAndDropCalendar
          {...(calendarProps as unknown as CalendarProps<CalendarViewEvent>)}
          resizable={!!props.handleEventResize}
          draggableAccessor={(event) => !props.isDraggable || props.isDraggable(event as TEvent)}
          resizableAccessor={(event) => view !== 'month' && (!props.isDraggable || props.isDraggable(event as TEvent))}
          onEventDrop={({ event, start, end }) => {
            props.handleEventDrop!(event as TEvent, new Date(start), new Date(end));
          }}
          onEventResize={({ event, start, end }) => {
            props.handleEventResize?.(event as TEvent, new Date(start), new Date(end));
          }}
        />
      ) : (
        <Calendar {...calendarProps} />
      )}
    </div>
  );
};
